import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { CurrentSyncFile } from "src/context/PraxisSoftwareSyncContext";
import "./praxisSoftwareSyncBlockingOverlay.scss";

/** Shown for the full duration of a sync run — every file in the batch, not just the
 *  first one. Deliberately has no close button, no Escape handling, and no backdrop
 *  click-to-dismiss (unlike Popup): the whole point is to stop staff from navigating
 *  away mid-sync and losing track of what's still running, which is what a per-file
 *  progress readout tucked inside one page's body couldn't prevent — it simply stopped
 *  being visible the moment they left that page while the sync kept going underneath. */
export function PraxisSoftwareSyncBlockingOverlay({
    currentFile,
    batchProgress,
}: {
    currentFile: CurrentSyncFile | null;
    batchProgress: { index: number; total: number } | null;
}) {
    const { t } = useTranslation();

    const overallPct = batchProgress
        ? Math.min(100, ((batchProgress.index + (currentFile?.progress ?? 0) / 100) / batchProgress.total) * 100)
        : null;

    return createPortal(
        <div className="pvs-sync-block-overlay" role="alert" aria-live="assertive">
            <div className="pvs-sync-block-overlay__card">
                <div className="loader-spinner" />
                <p className="pvs-sync-block-overlay__title">{t("praxisSoftwareSync.blockingTitle")}</p>

                {batchProgress && (
                    <p className="pvs-sync-block-overlay__step">
                        {t("praxisSoftwareSync.blockingStep", { index: batchProgress.index + 1, total: batchProgress.total })}
                    </p>
                )}

                {currentFile && <p className="pvs-sync-block-overlay__file">{currentFile.filename}</p>}

                {overallPct !== null && (
                    <div className="pvs-sync-block-overlay__bar-track">
                        <div className="pvs-sync-block-overlay__bar-fill" style={{ width: `${overallPct}%` }} />
                    </div>
                )}

                <p className="pvs-sync-block-overlay__warning">{t("praxisSoftwareSync.blockingWarning")}</p>
            </div>
        </div>,
        document.body,
    );
}
